import { EveRoutes } from '@ver-uds/e-verify';
import { Button } from '@ver-uds/react';
import { Breadcrumb, ButtonGroup, DateInput, FileInputControl, Form } from '@ver-uds/uswds-react';
import { addYears, isValid } from 'date-fns';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, Link, useHistory, useParams } from 'react-router-dom';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import ErrorBar from '../../components/ErrorBar';
import { ChildProfileState } from '../../components/ManageCompanies/ChildProfile';
import ErrorConstants from '../../constants/ErrorConstants';
import FullWidthLayout from '../../layouts/FullWidthLayout';
import { useAccountQuery, useSignMouDocumentMutation } from '../../services';
import MarketingQuestions from '../../components/Enrollment/CompanyInformation/MarketingQuestions/MarketingQuestions';
import Section from '../../components/Section';
import { MarketingFormData } from '../../components/ClientMou/constants';

type FormValues = {
  month: string;
  day: string;
  year: string;
  signedMouFile: FileList;
} & MarketingFormData;

const ClientMouUploadPage = (): JSX.Element => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const [mutate] = useSignMouDocumentMutation();

  const history = useHistory<ChildProfileState>();

  const [dateErrorMessage, setDateErrorMessage] = useState('');
  const [fileErrorMessage, setFileErrorMessage] = useState('');
  const { subAccountId } = useParams<{ subAccountId: string }>();
  const { data: accountData, isLoading } = useAccountQuery(subAccountId);

  const companyName = accountData?.companyInfo.companyName || 'Company';
  const companyId = accountData?.id;

  const companyProfileUrl = companyId
    ? generatePath(EveRoutes.COMPANY_CLIENTS_CLIENT_PROFILE_VIEW, { subAccountId: companyId })
    : '';

  const invalidMouSignDateMessage = 'MOU Sign Date must be a valid date';

  const fileSizeLimit = 2000000; // 2 MB

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      month: '',
      day: '',
      year: '',
    },
  });
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, register, watch } = formMethods;
  const parentValue = watch('marketingQuestionPrimary');

  const performSubmitValidations = (data: FormValues) => {
    const mouSignedDate = new Date(`${data.year}-${data.month}-${data.day}`);
    let isFormValid = true;

    // ensure that the date entered is a valid date
    if (!isValid(mouSignedDate)) {
      setDateErrorMessage(invalidMouSignDateMessage);
      isFormValid = false;
    }

    // ensure that the MOU Sign Date is within the last year
    const todayDate = new Date();
    const lastYearDate = addYears(todayDate, -1);
    if (mouSignedDate <= lastYearDate) {
      setDateErrorMessage('MOU Sign Date must be within the last year');
      isFormValid = false;
    }

    if (mouSignedDate > todayDate) {
      setDateErrorMessage('MOU Sign Date cannot be in the future');
      isFormValid = false;
    }

    // ensure that the file size is less than 2 MB
    const signedMouFileSize = data.signedMouFile[0].size;
    if (signedMouFileSize > fileSizeLimit) {
      setFileErrorMessage('File size must not exceed 2 MB');
      isFormValid = false;
    }

    // ensure that the document type is PDF (even though the component limits the user input to PDF selection)
    if (data.signedMouFile[0].type !== 'application/pdf') {
      setFileErrorMessage('File format must be a PDF');
      isFormValid = false;
    }

    return isFormValid;
  };

  const onSubmitForm = handleSubmit(async (data: FormValues) => {
    const isValidForm = performSubmitValidations(data);

    if (isValidForm) {
      const accountId = subAccountId;

      await mutate(
        {
          accountId,
          file: data.signedMouFile[0],
          marketingChannel: {
            primaryValue: data.marketingQuestionPrimary,
            secondaryValue: data.marketingQuestionSecondary,
          },
        },
        {
          // route back to profile page
          onSuccess: (response) => {
            history.push(generatePath(EveRoutes.COMPANY_CLIENTS_CLIENT_PROFILE_VIEW, { subAccountId: accountId }), {
              mouUploadSuccess: true,
            });
          },
          onError: (response) => {
            setErrorMessage(ErrorConstants.GENERAL_ERROR);
          },
        },
      );
    }
  });

  const { month, day, year } = watch();

  const validateInput = (value: string, inputField: string) => {
    if (month.trim().length === 0 && year.trim.length === 0 && day.trim().length === 0) {
      setDateErrorMessage(invalidMouSignDateMessage);
      return false;
    }

    if (value.trim().length === 0 || !Number.isFinite(Number(value))) {
      setDateErrorMessage(`You must enter a valid ${inputField}`);
      return false;
    }

    setDateErrorMessage('');
    return true;
  };

  return (
    <FullWidthLayout title="MOU Signature Upload">
      {isLoading ? (
        <div className="margin-y-4">
          Loading… <img src={LoaderIcon} alt="" className="height-2" />
        </div>
      ) : (
        <section className="padding-y-4 maxw-tablet-lg">
          <Breadcrumb className="padding-y-0">
            <Link to={EveRoutes.COMPANY_CLIENTS_VIEW}>Client Companies</Link>
            <Link to={companyProfileUrl}>{companyName}</Link>
            MOU Signature Upload
          </Breadcrumb>
          <ErrorBar errorMessage={errorMessage} />
          <h1 className="margin-top-1">MOU Signature Upload</h1>
          <p>
            Enter the date the client company signed the Memorandum of Understanding (MOU), and upload the document in
            PDF format. Once the signature page is uploaded, the client company will be activated unless there is a need
            for USCIS to review the client company.
          </p>
          <Form className="maxw-none margin-y-4" onSubmit={onSubmitForm} noValidate>
            <div className="tablet:grid-col-6">
              <DateInput
                id="mouSignDate"
                required
                hint="mm/dd/yyyy"
                label="Client Company MOU Sign Date"
                errorMessage={dateErrorMessage}
                dayRef={register({
                  validate: (value: string) => {
                    return validateInput(value, 'day');
                  },
                })}
                monthRef={register({
                  validate: (value: string) => {
                    return validateInput(value, 'month');
                  },
                })}
                yearRef={register({
                  validate: (value: string) => {
                    return validateInput(value, 'year');
                  },
                })}
              />
              <FileInputControl
                id="signed-mou-file"
                name="signedMouFile"
                required
                label="Upload Signed MOU"
                errorMessage={fileErrorMessage}
                hint="Must be a PDF"
                accept=".pdf"
                ref={register({
                  validate: (file: FileList) => {
                    if (file.length === 0) {
                      setFileErrorMessage('You must select a file');
                      return false;
                    }
                    setFileErrorMessage('');
                    return true;
                  },
                })}
              />
            </div>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <FormProvider {...formMethods}>
              <Section>
                <MarketingQuestions
                  parentValue={parentValue}
                  topDivider
                  headerTitle="Additional Information"
                  questionLabel="What is the primary reason your Client Company is enrolling in E-Verify?"
                />
                <hr />
              </Section>
            </FormProvider>
            <ButtonGroup className="margin-top-2">
              <Link to={companyProfileUrl} className="usa-button usa-button--outline">
                Cancel
              </Link>
              <Button variant="submit">Submit Signature Page</Button>
            </ButtonGroup>
          </Form>
        </section>
      )}
    </FullWidthLayout>
  );
};

export default ClientMouUploadPage;
